import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
	Button,
	Container,
	Paper,
	Stack,
	Typography,
	TableContainer,
	Table,
	TableRow,
	TableHead,
	TableCell,
	TableBody,
	TextField,
	Grid,
	Divider
} from '@mui/material';
import ReactToPrint from 'react-to-print';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faChevronCircleLeft,
	faCircleCheck,
	faLockOpen,
	faPrint,
	faSave
} from '@fortawesome/free-solid-svg-icons';
import { DataContext } from '../../../context/DataContext';
import { getRuta, isAccess, simpleMessage } from '../../../helpers/Helpers';
import { getToken } from '../../../api/Account';
import { PrintReport } from '../../../components/PrintReport';
import {
	cerrarArqueoAsync,
	getArqueoByIdAsync,
	getDataArqueoUserAsync,
	reabrirArqueoUserAsync,
	updateArqueoUserAsync
} from '../../../api/ArqueoCaja';
import moment from 'moment';
import { getTipoCambioHoyAsync } from '../../../api/TipoCambio';
import GastosGestorModal from './GastosGestorModal';
import TransferenciasGestorModal from './TransferenciasGestorModal';

/* ================== CONSTANTES Y HELPERS ================== */
const formatter = new Intl.NumberFormat('es-NI', {
	style: 'currency',
	currency: 'NIO'
});

const buildDetalle = (data) => {
	return data.map((item) => ({
		id: item.demonimacion?.id || item.id,
		description: item.demonimacion?.description || item.description,
		valor: item.demonimacion?.valor || item.valor,
		cantidad: item.cantidad || 0,
		total: item.total || 0
	}));
};

const toNumber = (value) => Number(value) || 0;
const formatDate = (date) => moment(date).format('YYYY-MM-DD');

const ArqueoUserDetails = () => {
	/* ================== ESTADOS ================== */
	const [arqueo, setArqueo] = useState(null);
	const [dataArqueoUser, setDataArqueoUser] = useState(null);
	const [detalleArqueo, setDetalleArqueo] = useState([]);
	const [tcCompra, setTcCompra] = useState(0);

	// Estados de formulario
	const [saldoInicial, setSaldoInicial] = useState('');
	const [montoDepositos, setMontoDepositos] = useState('');
	const [observaciones, setObservaciones] = useState('');
	const [gastosModalOpen, setGastosModalOpen] = useState(false);
	const [transferenciasModalOpen, setTransferenciasModalOpen] = useState(false);

	const [localReload, setLocalReload] = useState(false);

	/* ================== CONTEXTO Y ROUTING ================== */
	const { id } = useParams();
	const navigate = useNavigate();
	const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);
	const token = getToken();
	const ruta = getRuta();
	const compRef = useRef();

	/* ================== CÁLCULOS SIMPLIFICADOS ================== */
	const isCerrado = arqueo?.estadoArqueo === 2;
	// console.log('🚀 ~ isCerrado:', isCerrado);

	// Valores numéricos
	const saldoInicialN = toNumber(saldoInicial);
	const gastosN = toNumber(dataArqueoUser?.montoGastos);
	const depositosN = toNumber(montoDepositos);

	const recuperacionEfectivoN = toNumber(dataArqueoUser?.montoRecuperacionEfectivo);
	const abonosN = toNumber(dataArqueoUser?.montoRecuperacionTransferencia);
	const desembolsosN = toNumber(dataArqueoUser?.montoDesembolsos);

	// Totales
	const totalBilletaje = detalleArqueo.reduce((sum, item) => sum + item.total, 0);
	const totalIngresos = saldoInicialN + recuperacionEfectivoN + abonosN;
	const totalEgresos = gastosN + desembolsosN + depositosN;
	// Solo la recuperación en efectivo representa dinero físico en caja;
	// los abonos por transferencia no forman parte de la entrega.
	const entrega = saldoInicialN + recuperacionEfectivoN - totalEgresos;
	const diferencia = totalBilletaje - entrega;

	/* ================== HANDLERS ================== */
	const handleError = (result) => {
		setIsLoading(false);
		if (result.error === 'Unauthorized') {
			setIsUnautorized(true);
			return true;
		}
		simpleMessage(result.error, 'error');
		return true;
	};

	const onChangeMonto = (value, setter) => {
		if (value === '' || /^\d*\.?\d*$/.test(value)) {
			setter(value);
		}
	};

	const onEditDetalle = useCallback(
		(id, valor, cantidad) => {
			const qty = toNumber(cantidad);
			setDetalleArqueo((prev) =>
				prev.map((item) => {
					if (item.id === id) {
						let total = valor * qty;
						if (item.id === 2) total *= tcCompra; // Ajuste para moneda extranjera
						total = Math.round(total * 100) / 100;
						return { ...item, cantidad: qty, total };
					}
					return item;
				})
			);
		},
		[tcCompra]
	);

	const saveArqueo = async () => {
		const data = {
			id,
			aqueoUserDetails: detalleArqueo.map((d) => ({
				demonimacionId: d.id,
				cantidad: d.cantidad,
				total: d.total
			})),
			saldoInicial: saldoInicialN,
			recuperacion: recuperacionEfectivoN,
			recuperacionTransferencia: abonosN,
			depositos: depositosN,
			montoGastos: gastosN,
			montoDesembolso: desembolsosN,
			diferencia,
			efectivo: totalBilletaje,
			observaciones: observaciones.toUpperCase(),
			tipoCambio: tcCompra
		};
		// console.log('🚀 ~ data:', data);

		setIsLoading(true);
		const res = await updateArqueoUserAsync(token, data);
		if (!res.statusResponse && handleError(res)) return;

		setDetalleArqueo(buildDetalle(detalleArqueo));
		simpleMessage('Arqueo actualizado con éxito', 'success');
		setIsLoading(false);
	};

	const onCloseArqueo = async () => {
		setIsLoading(true);
		const result = await cerrarArqueoAsync(token, id);
		if (!result.statusResponse && handleError(result)) return;

		setLocalReload(!localReload);
		simpleMessage('Arqueo cerrado con éxito', 'success');
		setIsLoading(false);
	};

	const onReopenArqueo = async () => {
		setIsLoading(true);
		const result = await reabrirArqueoUserAsync(token, id);
		if (!result.statusResponse && handleError(result)) return;

		setLocalReload(!localReload);
		simpleMessage('Arqueo reabierto con éxito', 'success');
		setIsLoading(false);
	};

	/* ================== EFFECTS ================== */
	useEffect(() => {
		const init = async () => {
			setIsLoading(true);
			await Promise.all([getArqueoById(), loadTipoCambio()]);
			setIsLoading(false);
		};
		init();
	}, [localReload]);

	const getArqueoById = async () => {
		const result = await getArqueoByIdAsync(token, id);
		if (!result.statusResponse) return handleError(result);

		const arqueoData = result.data.result;
		// console.log('🚀 ~ arqueoData:', arqueoData);

		setArqueo(arqueoData);
		setDetalleArqueo(buildDetalle(arqueoData.aqueoUserDetails));
		setSaldoInicial(arqueoData.saldoInicial.toString());
		setMontoDepositos(arqueoData.depositos.toString());
		setObservaciones(arqueoData.observaciones || '');

		if (arqueoData.estadoArqueo === 1) {
			loadUserData(arqueoData.gestor.id, formatDate(arqueoData.fechaArqueo));
		}
	};

	const loadTipoCambio = async () => {
		const res = await getTipoCambioHoyAsync(token);
		if (!res.statusResponse && handleError(res)) return;
		setTcCompra(res.data.compra);
	};

	const loadUserData = async (userId, fecha) => {
		const res = await getDataArqueoUserAsync(token, { userId, fecha });
		if (!res.statusResponse && handleError(res)) return;
		setDataArqueoUser(res.data.result);
	};

	/* ================== RENDER HELPERS ================== */
	const renderValue = (closedValue, openValue) => {
		return isCerrado ? closedValue : openValue;
	};

	if (!arqueo) return null;

	/* ================== ESTILOS ================== */
	const pageStyle = `
		@media print {
			html, body { height: initial !important; overflow: initial !important; }
			.pagebreak { page-break-before: always; }
		}
		@page { margin: 8mm; }
	`;

	const buttonStyles = {
		close: { color: '#00a152', borderColor: '#00a152', padding: 13, borderRadius: 30 },
		print: { color: '#2979ff', borderColor: '#2979ff', padding: 13, borderRadius: 30 },
		save: { color: '#ffc400', borderColor: '#ffc400', padding: 13, borderRadius: 30 }
	};

	/* ================== RENDER ================== */
	return (
		<div>
			<Container maxWidth="xl">
				<Paper sx={{ p: 2 }}>
					{/* HEADER */}
					<Stack
						direction={{ xs: 'column', sm: 'row' }}
						alignItems="center"
						spacing={1}
						justifyContent="space-between"
						mb={2}
					>
						<Stack
							direction={{ xs: 'column', sm: 'row' }}
							alignItems="center"
							spacing={1}
						>
							<Button
								onClick={() => navigate(`${ruta}/arqueouser`)}
								variant="outlined"
							>
								<FontAwesomeIcon icon={faChevronCircleLeft} />
								&nbsp;Regresar
							</Button>
							<Typography color="#00a152" fontWeight="bold">
								Detalle Arqueo: {arqueo.gestor.fullName}
							</Typography>
						</Stack>
						<Stack spacing={1} direction="row">
							<Typography color="#2979ff" fontWeight="bold">
								Sucursal:
							</Typography>
							<Typography color="#00a152" fontWeight="bold">
								{arqueo.sucursal.description}
							</Typography>
						</Stack>
					</Stack>

					<Divider sx={{ mb: 2 }} />

					{/* CONTENIDO PRINCIPAL */}
					<Grid container spacing={2}>
						{/* COLUMNA IZQUIERDA - RESUMEN */}
						<Grid item xs={12} md={6}>
							<Paper sx={{ p: 2 }}>
								<Stack spacing={1}>
									<Stack direction="row" justifyContent="space-between">
										<Typography color="#2979ff" fontWeight="bold">
											Arqueo #: {arqueo.id}
										</Typography>
										<Stack spacing={1} direction="row">
											<Typography color="#2979ff" fontWeight="bold">
												Fecha:
											</Typography>
											<Typography color="#00a152" fontWeight="bold">
												{moment(arqueo.fechaCreacion).format('L')}
											</Typography>
										</Stack>
									</Stack>

									<Divider />

									{/* INGRESOS */}
									<Stack spacing={1}>
										<TextField
											fullWidth
											size="small"
											label="Saldo Inicial"
											value={renderValue(arqueo.saldoInicial, saldoInicial)}
											onChange={(e) =>
												onChangeMonto(e.target.value, setSaldoInicial)
											}
											disabled={isCerrado}
										/>
										<Stack direction="row" justifyContent="space-between">
											<Typography>Recuperación en Efectivo</Typography>
											<Typography fontWeight="bold">
												{formatter.format(
													renderValue(
														arqueo.recuperacion,
														recuperacionEfectivoN
													)
												)}
											</Typography>
										</Stack>
										<Stack direction="row" justifyContent="space-between">
											<Typography>Recuperación en Depósitos</Typography>
											<Typography fontWeight="bold">
												{formatter.format(
													renderValue(
														arqueo.recuperacionTransferencia,
														abonosN
													)
												)}
											</Typography>
										</Stack>
										<Button
											variant="outlined"
											size="small"
											style={{ color: '#2979ff', borderColor: '#2979ff' }}
											onClick={() => setTransferenciasModalOpen(true)}
										>
											Ver Transferencias
										</Button>
										<Stack direction="row" justifyContent="space-between">
											<Typography fontWeight="bold">
												Total Ingresos
											</Typography>
											<Typography fontWeight="bold" color="#00a152">
												{formatter.format(
													renderValue(arqueo.totalIngresos, totalIngresos)
												)}
											</Typography>
										</Stack>
									</Stack>

									<Divider />

									{/* EGRESOS */}
									<Stack spacing={1}>
										{/* <TextField
											fullWidth
											size="small"
											label="Monto Depositos"
											value={renderValue(arqueo.depositos, montoDepositos)}
											onChange={(e) =>
												onChangeMonto(e.target.value, setMontoDepositos)
											}
											disabled={isCerrado}
										/> */}
										<Stack direction="row" justifyContent="space-between">
											<Typography>Monto Gastos</Typography>
											<Typography fontWeight="bold">
												{formatter.format(
													renderValue(arqueo.montoGastos, gastosN)
												)}
											</Typography>
										</Stack>
										<Button
											variant="outlined"
											size="small"
											style={{ color: '#f50057', borderColor: '#f50057' }}
											onClick={() => setGastosModalOpen(true)}
										>
											Ver Gastos
										</Button>
										<Stack direction="row" justifyContent="space-between">
											<Typography>Total Desembolsos</Typography>
											<Typography fontWeight="bold">
												{formatter.format(
													renderValue(
														arqueo.montoDesembolso,
														desembolsosN
													)
												)}
											</Typography>
										</Stack>
										<Stack direction="row" justifyContent="space-between">
											<Typography fontWeight="bold">Total Egresos</Typography>
											<Typography fontWeight="bold" color="#f50057">
												{formatter.format(
													renderValue(arqueo.totalEgresos, totalEgresos)
												)}
											</Typography>
										</Stack>
									</Stack>

									<Divider />

									{/* RESULTADOS */}
									<Stack spacing={1}>
										<Stack direction="row" justifyContent="space-between">
											<Typography fontWeight="bold">Entrega</Typography>
											<Typography fontWeight="bold" color="#2979ff">
												{formatter.format(
													renderValue(arqueo.efectivo, entrega)
												)}
											</Typography>
										</Stack>
										<Stack direction="row" justifyContent="space-between">
											<Typography fontWeight="bold">Diferencia</Typography>
											<Typography
												fontWeight="bold"
												color={diferencia === 0 ? '#00a152' : '#ffc400'}
											>
												{formatter.format(
													renderValue(
														arqueo.diferencia,
														totalBilletaje - entrega
													)
												)}
											</Typography>
										</Stack>
									</Stack>

									<TextField
										fullWidth
										size="small"
										label="Observaciones"
										value={observaciones}
										onChange={(e) =>
											setObservaciones(e.target.value.toUpperCase())
										}
										multiline
										rows={2}
										disabled={isCerrado}
									/>
								</Stack>
							</Paper>
						</Grid>

						{/* COLUMNA DERECHA - BILLETAJE */}
						<Grid item xs={12} md={6}>
							<Paper sx={{ p: 2 }}>
								<Stack spacing={2}>
									<Stack
										direction="row"
										justifyContent="space-between"
										alignItems="center"
									>
										<Typography fontWeight="bold">Billetaje</Typography>
										<Typography variant="body2" color="#ffc400">
											Tipo de Cambio: {formatter.format(tcCompra)}
										</Typography>
									</Stack>

									<Divider />

									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell sx={{ py: 0.25 }}>
														Denominación
													</TableCell>
													<TableCell align="center" sx={{ py: 0.25 }}>
														Cantidad
													</TableCell>
													<TableCell align="center" sx={{ py: 0.25 }}>
														Total
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{detalleArqueo.map((row) => (
													<TableRow
														key={row.id}
														sx={{ '& td': { py: 0.25, px: 1 } }}
													>
														<TableCell>{row.description}</TableCell>
														<TableCell align="center">
															<TextField
																size="small"
																value={row.cantidad}
																onChange={(e) =>
																	onEditDetalle(
																		row.id,
																		row.valor,
																		e.target.value
																	)
																}
																inputProps={{
																	style: {
																		padding: '2px 6px',
																		height: 26,
																		textAlign: 'center'
																	}
																}}
																disabled={isCerrado}
															/>
														</TableCell>
														<TableCell align="center">
															{formatter.format(row.total)}
														</TableCell>
													</TableRow>
												))}
												<TableRow
													sx={{
														'& td': {
															py: 0.25,
															px: 1,
															fontWeight: 'bold',
															color: '#00a152'
														}
													}}
												>
													<TableCell colSpan={2}>
														TOTAL BILLETAJE
													</TableCell>
													<TableCell align="center">
														{formatter.format(totalBilletaje)}
													</TableCell>
												</TableRow>
											</TableBody>
										</Table>
									</TableContainer>
								</Stack>
							</Paper>
						</Grid>

						{/* BOTONES */}
						<Grid item xs={12}>
							<Stack direction="row" spacing={2}>
								{isCerrado ? (
									<>
										{isAccess(access, 'ACAJA PRINT') && (
											<ReactToPrint
												trigger={() => (
													<Button
														variant="outlined"
														fullWidth
														style={buttonStyles.print}
													>
														<FontAwesomeIcon
															icon={faPrint}
															style={{
																marginRight: 10,
																fontSize: 20
															}}
														/>
														Imprimir Arqueo
													</Button>
												)}
												content={() => compRef.current}
												pageStyle={pageStyle}
											/>
										)}
										{isAccess(access, 'REABRIR ARQUEO') && (
											<Button
												variant="outlined"
												onClick={onReopenArqueo}
												fullWidth
												style={buttonStyles.close}
											>
												<FontAwesomeIcon
													icon={faLockOpen}
													style={{ marginRight: 5, fontSize: 20 }}
												/>
												Reabrir Arqueo
											</Button>
										)}
									</>
								) : (
									<>
										<Button
											variant="outlined"
											onClick={onCloseArqueo}
											fullWidth
											style={buttonStyles.close}
										>
											<FontAwesomeIcon
												icon={faCircleCheck}
												style={{ marginRight: 5, fontSize: 20 }}
											/>
											Cerrar Arqueo
										</Button>
										<Button
											variant="outlined"
											onClick={saveArqueo}
											fullWidth
											style={buttonStyles.save}
										>
											<FontAwesomeIcon
												icon={faSave}
												style={{ marginRight: 5, fontSize: 20 }}
											/>
											Actualizar Arqueo
										</Button>
									</>
								)}
							</Stack>
						</Grid>
					</Grid>
				</Paper>
			</Container>

			{/* COMPONENTE DE IMPRESIÓN (OCULTO) */}
			<div style={{ display: 'none' }}>
				<PrintReport
					ref={compRef}
					titulo={'Arqueo de Usuario'}
					fecha={`Fecha: ${moment().format('L')}`}
				>
					<Stack spacing={1} direction="row" justifyContent="space-between">
						<Typography color={'#2979ff'} fontWeight={'bold'}>
							{` Arqueo #: ${arqueo.id} `}
						</Typography>

						<Stack spacing={1} direction="row">
							<Typography color={'#2979ff'} fontWeight={'bold'}>
								Fecha:
							</Typography>
							<Typography color={'#00a152'} fontWeight={'bold'}>
								{moment(arqueo.fechaCreacion).format('L')}
							</Typography>
						</Stack>
					</Stack>

					<Stack spacing={1} direction="row" justifyContent="space-between">
						<Typography color={'#00a152'} fontWeight={'bold'}>
							{`Gestor: ${arqueo.gestor.fullName}`}
						</Typography>
						<Stack spacing={1} direction="row">
							<Typography color={'#2979ff'} fontWeight={'bold'}>
								Sucursal:
							</Typography>
							<Typography color={'#00a152'} fontWeight={'bold'}>
								{arqueo.sucursal.description}
							</Typography>
						</Stack>
					</Stack>

					<Divider
						sx={{
							my: 1,
							'@media print': {
								borderBottomWidth: 2,
								borderColor: '#000'
							}
						}}
					/>

					<Grid container spacing={2}>
						<Grid item sm={6}>
							<Stack
								direction="row"
								justifyContent="space-between"
								color="#ffc400"
								mt={2}
							>
								<Typography fontWeight="bold">Tipo de Cambio</Typography>
								<Typography fontWeight="bold">
									{formatter.format(arqueo.tipoCambio || 0)}
								</Typography>
							</Stack>

							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Saldo Inicial</Typography>
								<Typography fontWeight="bold">
									{formatter.format(
										isCerrado
											? arqueo.saldoInicial
											: dataArqueoUser?.saldoInicial || 0
									)}
								</Typography>
							</Stack>

							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Recuperación en Efectivo</Typography>
								<Typography fontWeight="bold">
									{formatter.format(
										isCerrado ? arqueo.recuperacion : recuperacionEfectivoN
									)}
								</Typography>
							</Stack>

							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Recuperación en Depósitos</Typography>
								<Typography fontWeight="bold">
									{formatter.format(
										isCerrado ? arqueo.recuperacionTransferencia : abonosN
									)}
								</Typography>
							</Stack>

							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Total Ingresos</Typography>
								<Typography fontWeight="bold" color="#00a152">
									{formatter.format(
										isCerrado ? arqueo.totalIngresos : totalIngresos
									)}
								</Typography>
							</Stack>

							<Divider
								sx={{
									my: 1,
									'@media print': {
										borderBottomWidth: 2,
										borderColor: '#000'
									}
								}}
							/>
							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Entrega</Typography>
								<Typography fontWeight="bold" color="#00a152">
									{formatter.format(
										// Solo la recuperación en efectivo representa dinero
										// físico en caja; los abonos por transferencia no
										// forman parte de la entrega.
										isCerrado
											? (arqueo.saldoInicial || 0) +
													(arqueo.recuperacion || 0) -
													((arqueo.montoGastos || 0) +
														(arqueo.montoDesembolso || 0) +
														(arqueo.depositos || 0))
											: entrega
									)}
								</Typography>
							</Stack>
						</Grid>

						<Grid item sm={6}>
							<Stack direction="row" justifyContent="space-between" mt={2}>
								<Typography fontWeight="bold">Monto Depositos</Typography>
								<Typography fontWeight="bold">
									{formatter.format(
										isCerrado ? arqueo.depositos : montoDepositos || 0
									)}
								</Typography>
							</Stack>

							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Monto Gastos</Typography>
								<Typography fontWeight="bold">
									{formatter.format(
										isCerrado ? arqueo.montoGastos : gastosN
									)}
								</Typography>
							</Stack>

							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Total Desembolsos</Typography>
								<Typography fontWeight="bold">
									{formatter.format(
										isCerrado
											? arqueo.montoDesembolso
											: dataArqueoUser?.montoDesembolsos || 0
									)}
								</Typography>
							</Stack>

							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Total Egresos</Typography>
								<Typography fontWeight="bold" color="#f50057">
									{formatter.format(
										isCerrado
											? arqueo.totalEgresos
											: gastosN +
													(dataArqueoUser?.montoDesembolsos || 0) +
													(Number(montoDepositos) || 0)
									)}
								</Typography>
							</Stack>
							<Divider
								sx={{
									my: 1,
									'@media print': {
										borderBottomWidth: 2,
										borderColor: '#000'
									}
								}}
							/>
							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Diferencia</Typography>
								<Typography fontWeight="bold" color="#f50057">
									{formatter.format(arqueo.diferencia)}
								</Typography>
							</Stack>
						</Grid>

						<Grid item sm={12}>
							<Typography fontWeight="bold">{`Observaciones: ${arqueo.observaciones}`}</Typography>
						</Grid>

						<Grid item sm={12}>
							<TableContainer>
								<Table aria-label="simple table" size="small">
									<TableHead>
										<TableRow>
											<TableCell
												align="left"
												className="text-primary fw-bold"
											>
												Denominacion
											</TableCell>
											<TableCell
												align="center"
												className="text-primary fw-bold"
											>
												Billetaje
											</TableCell>
											<TableCell
												align="center"
												className="text-primary fw-bold"
											>
												C$
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{arqueo.aqueoUserDetails.map((row) => {
											const { cantidad, demonimacion, id, total } = row;

											return (
												<TableRow key={id}>
													<TableCell align="left" className="text-dark">
														{demonimacion.description}
													</TableCell>
													<TableCell align="center" className="text-dark">
														{cantidad}
													</TableCell>
													<TableCell align="center" className="text-dark">
														{formatter.format(total)}
													</TableCell>
												</TableRow>
											);
										})}

										<TableRow
											sx={{
												'& td': {
													py: 0.25,
													px: 1,
													fontWeight: 'bold',
													color: '#00a152'
												}
											}}
										>
											<TableCell colSpan={2}>TOTAL BILLETAJE</TableCell>
											<TableCell align="center">
												{formatter.format(totalBilletaje)}
											</TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</TableContainer>
						</Grid>
					</Grid>

					<Stack marginTop={20} direction="row" justifyContent="space-between">
						<Typography
							color={'#2979ff'}
							fontWeight={'bold'}
							align="center"
							style={{ textDecoration: 'overline' }}
						>
							{` Entrega #: ${arqueo.gestor.fullName} `}
						</Typography>
						<Typography
							color={'#2979ff'}
							fontWeight={'bold'}
							align="center"
							style={{ textDecoration: 'overline' }}
						>
							{` Recibe #: ${arqueo.recibidoPor.fullName} `}
						</Typography>
					</Stack>
				</PrintReport>
			</div>

			<GastosGestorModal
				open={gastosModalOpen}
				onClose={setGastosModalOpen}
				gestorId={arqueo.gestor.id}
				sucursalId={arqueo.sucursal.id}
				fecha={arqueo.fechaArqueo}
				readOnly={isCerrado}
				onChange={() => loadUserData(arqueo.gestor.id, formatDate(arqueo.fechaArqueo))}
			/>

			<TransferenciasGestorModal
				open={transferenciasModalOpen}
				onClose={setTransferenciasModalOpen}
				gestorId={arqueo.gestor.id}
				fecha={arqueo.fechaArqueo}
			/>
		</div>
	);
};

export default ArqueoUserDetails;
